import React from 'react';
import { IdsButton, IdsCard, IdsLink } from '@investec/ids-web-react';
import downloadIcon from '../../assets/images/download.svg';
import envConfig from '../../config/env-config';
import { productTypes } from '../../utils/productHelpers';
import Accordion from '../Accordion/Accordion';
import '../NewAccount/SummaryBox/summaryBox.scss';
import { faqs, getMaturitySteps, getSummarySections, maturityBenefits } from './disaMaturityContent';
import { useFeatureFlags } from '../FeatureFlags/withFeatureFlags';
import './disaMaturityPage.scss';

const formatRate = (rate) => Number(rate || 0).toFixed(2);

const getDocumentUrl = (fileName) => {
    if (!fileName) {
        return '#';
    }

    return /^https?:\/\//.test(fileName) ? fileName : `${envConfig.links.documentUrl}${fileName}`;
};

const getDisaProduct = (products = []) => products.find((product) =>
    product.type === productTypes.InstantAccessCashISA ||
    product.productType === productTypes.InstantAccessCashISA
);

const DisaHeroRateCard = ({ product, isMatured, enableUpdatedMaturityCopy }) => {
    const aerRate = product?.aerRate;
    const grossRate = product?.rate;

    return (
        <section className="disa-hero">
            <h1>{isMatured ? 'Instant Access Cash ISA account' : 'Your Fixed Rate Cash ISA is maturing soon'}</h1>
            {!isMatured && (
                <p className="disa-hero__intro">
                    {enableUpdatedMaturityCopy
                        ? <>On your maturity date, we&apos;ll move your savings including any interest earned into an Instant Access Cash ISA.</>
                        : <>On your maturity date, we&apos;ll transfer your savings into an Instant Access Cash ISA and pay any interest earned to your linked account.</>
                    }
                </p>
            )}
            <IdsCard className="disa-rate-card" aria-label="Instant Access Cash ISA rates">
                <span className="disa-rate-card__name">Instant Access Cash ISA</span>
                <span className="disa-rate-card__rate disa-rate-card__rate--aer">
                    <strong>{formatRate(aerRate)}%</strong>
                    <span>AER*</span>
                </span>
                <span className="disa-rate-card__divider" aria-hidden="true" />
                <span className="disa-rate-card__rate disa-rate-card__rate--gross">
                    <strong>{formatRate(grossRate)}%</strong>
                    <span>gross</span>
                </span>
            </IdsCard>
        </section>
    );
};

const DisaMaturitySteps = ({ enableUpdatedMaturityCopy }) => {
    const maturitySteps = getMaturitySteps(enableUpdatedMaturityCopy);

    return (
    <section className="disa-section disa-section--muted">
        <div className="disa-section__inner">
            <h2>What happens at maturity?</h2>
            <p className="disa-section__intro">Here&apos;s what happens when your Fixed Rate Cash ISA reaches maturity.</p>
            <div className="disa-steps">
                {maturitySteps.map((step, index) => (
                    <article className="disa-steps__item" key={step.title}>
                        <h3>
                            {step.title}
                            {index < maturitySteps.length - 1 && (
                                <span className="disa-steps__arrow disa-steps__arrow--desktop" aria-hidden="true">
                                    <svg width="19" height="42" viewBox="0 0 19 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 41L17 21L1 1" stroke="#9AC3C5" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </span>
                            )}
                        </h3>
                        <p>{step.body}</p>
                        {index < maturitySteps.length - 1 && (
                            <span className="disa-steps__arrow disa-steps__arrow--mobile" aria-hidden="true">
                                <svg width="19" height="42" viewBox="0 0 19 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 41L17 21L1 1" stroke="#9AC3C5" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                        )}
                    </article>
                ))}
            </div>
            <h3 className="disa-benefits__heading">Once your Instant Access Cash ISA is open</h3>
            <ul className="disa-benefits">
                {maturityBenefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                ))}
            </ul>
        </div>
    </section>
    );
};

const DisaSummaryBox = ({ product }) => {
    const summarySections = getSummarySections(product);

    return (
        <section className="disa-section disa-summary-section summary-box">
            <h2 className="summary-box__heading">Summary box - everything you need to know</h2>
            <div className="disa-summary">
                <Accordion items={summarySections} />
            </div>
        </section>
    );
};

const DisaImportantDocuments = ({ product }) => {
    const documents = [
        {
            label: 'Instant Access Cash ISA summary box & key features',
            href: getDocumentUrl(product?.summaryBoxFileName),
        },
        {
            label: 'Product terms - Instant Access Cash ISA',
            href: getDocumentUrl(product?.productTermsFileName),
        },
        {
            label: 'General terms',
            href: getDocumentUrl(window.raeEnv?.generalTermsFileName || ''),
        },
        {
            label: 'FSCS Information Sheet',
            href: 'https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf',
        },
    ];

    return (
        <section className="disa-section disa-documents">
            <div className="disa-section__inner">
                <h2>Important documents</h2>
                <p>Please download and read the following documents:</p>
                <ul className="disa-documents__links">
                    {documents.map((document) => (
                        <li key={document.label}>
                            <IdsLink href={document.href} target="_blank" rel="noreferrer" size="sm">
                                <img src={downloadIcon} alt="" className="disa-documents__icon" />
                                {document.label}
                            </IdsLink>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

const DisaFaqs = () => (
    <section className="disa-section disa-section--muted disa-faqs">
        <div className="disa-section__inner">
            <h2>Frequently asked questions</h2>
            <div className="disa-faqs__grid">
                {faqs.map((faq) => (
                    <article className="disa-faqs__item" key={faq.title}>
                        <h3>{faq.title}</h3>
                        <p>{faq.body}</p>
                    </article>
                ))}
            </div>
        </div>
    </section>
);

const DisaQuestionsCta = () => (
    <section className="disa-section disa-questions">
        <div className="disa-section__inner">
            <h2>Do you have any questions?</h2>
            <p>If you have accessibility needs, please contact us and we&apos;ll be happy to help.</p>
            <div className="disa-questions__actions">
                <IdsButton className="disa-questions__button" appearance="default" size="sm" onClick={() => { window.location.href = `${envConfig.links.productPageUrl}/faqs`; }}>
                    View our FAQs
                </IdsButton>
                <IdsButton className="disa-questions__button" appearance="default" size="sm" onClick={() => { window.location.href = '/contact-us'; }}>
                    Start live chat
                </IdsButton>
            </div>
        </div>
    </section>
);

const DisaFootnotes = () => (
    <section className="disa-footnotes">
        <p>* AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest was paid and compounded once a year.</p>
        <p>The AER is intended to be an indicative rate to help you compare the return on different savings products.</p>
        <p>^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.</p>
    </section>
);

const DisaMaturityPage = ({ products = [], isMatured = false }) => {
    const [featureEnabled] = useFeatureFlags();
    const enableUpdatedMaturityCopy = featureEnabled('EnableDashboardMaturityCopyUpdate');
    const disaProduct = getDisaProduct(products);

    return (
        <main className="disa-maturity-page">
            <DisaHeroRateCard
                product={disaProduct}
                isMatured={isMatured}
                enableUpdatedMaturityCopy={enableUpdatedMaturityCopy}
            />
            {!isMatured && <DisaMaturitySteps enableUpdatedMaturityCopy={enableUpdatedMaturityCopy} />}
            <DisaSummaryBox product={disaProduct} />
            <DisaImportantDocuments product={disaProduct} />
            <DisaFaqs />
            <DisaQuestionsCta />
            <DisaFootnotes />
        </main>
    );
};

export default DisaMaturityPage;
